import React, { useEffect, useMemo, useState } from "react";
import { schoolAPI } from "../services/api";

const parseSources = (raw) => {
  if (!raw) return [];
  const parts = String(raw)
    .split(/[|;]/)
    .map((item) => item.trim())
    .filter(Boolean);
  const seen = new Set();
  const deduped = [];
  parts.forEach((url) => {
    if (seen.has(url)) return;
    seen.add(url);
    deduped.push(url);
  });
  return deduped.slice(0, 6);
};

const extractDomain = (url) => {
  if (!url) return "Source";
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, "") || "Source";
  } catch (error) {
    return url.replace(/^https?:\/\//, "").split("/")[0] || "Source";
  }
};

const buildFallbackImage = (url) => {
  const domain = extractDomain(url).slice(0, 24);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="600" height="300">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#FDF6EF"/>
          <stop offset="50%" stop-color="#FFF8F4"/>
          <stop offset="100%" stop-color="#F9E7DB"/>
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#g)"/>
      <text x="30" y="170" font-family="Satoshi, Arial, sans-serif" font-size="26" fill="#A66A3A" letter-spacing="2">
        ${domain.toUpperCase()}
      </text>
    </svg>
  `.trim();
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ScholarshipSourcesCarousel = ({ sources, lastVerified }) => {
  const cards = useMemo(() => parseSources(sources), [sources]);
  const [metadata, setMetadata] = useState({});

  if (cards.length === 0) {
    return null;
  }

  const verifiedLabel = formatDate(lastVerified);

  useEffect(() => {
    let isMounted = true;
    if (cards.length === 0) return () => {};

    schoolAPI
      .getSourceMetadata(cards)
      .then((response) => {
        if (!isMounted) return;
        if (response?.success && response.data) {
          setMetadata(response.data);
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [cards]);

  return (
    <div className="space-y-4">
      <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        {cards.map((url) => {
          const imageUrl = metadata?.[url]?.image || null;
          const fallbackImage = buildFallbackImage(url);
          return (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="group flex-shrink-0 min-w-[240px] sm:min-w-[260px] md:min-w-[280px] lg:min-w-[300px] rounded-xl border bg-white shadow-sm transition hover:shadow-md hover:border-[#E6D8CD] overflow-hidden"
            >
              <div className="h-[110px] w-full bg-[#F9F4EE] overflow-hidden">
                <img
                  src={imageUrl || fallbackImage}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  onError={(event) => {
                    if (event.currentTarget.src !== fallbackImage) {
                      event.currentTarget.src = fallbackImage;
                    }
                  }}
                />
              </div>
              <div className="p-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-gray-400">
                  {extractDomain(url)}
                </p>
                <p className="mt-2 text-sm font-semibold text-[#111827]">
                  Signing day announcement
                </p>
                <p className="mt-1 text-xs text-gray-500">View source</p>
                {verifiedLabel && (
                  <p className="mt-3 text-[11px] text-gray-400">
                    Last verified {verifiedLabel}
                  </p>
                )}
              </div>
            </a>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">
        Some values are estimated from available commitments. Always verify with
        the college program.
      </p>
    </div>
  );
};

export default ScholarshipSourcesCarousel;
